import SEO from '../components/seo/SEO';

export default function Privacy() {
  return (
    <main className="min-h-screen px-4 py-10">
      <SEO 
        title="Privacy Policy - PromptShare"
        description="Learn how PromptShare handles, processes, and protects your personal data and privacy."
        url="https://promptshare.pro/privacy"
      />
      <div className="max-w-4xl mx-auto space-y-8">
        <header className="space-y-2">
          <h1>PromptShare Privacy Policy</h1>
          <p>Effective Date: 20 October 2025</p>
        </header>

        <section className="space-y-2">
          <h2>1. Controller Statement</h2>
          <p>
            PromptShare administers this platform for the exchange of prompt concepts and ancillary
            creative commentary. Within applicable data protection frameworks, PromptShare functions as
            the data controller for personal information collected through registration flows, account
            management utilities, communication channels, and technical instrumentation supporting the
            service.
          </p>
        </section>

        <section className="space-y-2">
          <h2>2. Categories of Data Processed</h2>
          <p>
            The platform may process identification data, contact particulars, authentication artefacts,
            usage telemetry, device descriptors, content metadata, and moderation submissions. PromptShare
            does not intentionally solicit sensitive classifications of data or material relating to minors
            under the age threshold defined by governing law; any such content should be removed by the
            uploader without delay.
          </p>
        </section>

        <section className="space-y-2">
          <h2>3. Collection Mechanisms</h2>
          <p>
            Information is obtained directly when you furnish it, automatically through cookies and similar
            diagnostic tools, and indirectly from integrated service vendors who supply payment, security,
            or analytics capabilities. Submission of content that embeds third-party personal data occurs
            at the sole initiative and risk of the uploader, who must possess appropriate authorisations.
          </p>
        </section>

        <section className="space-y-2">
          <h2>4. Processing Objectives and Legal Bases</h2>
          <p>
            Data is processed to operate accounts, authenticate access, host submissions, maintain service
            quality, administer moderation workflows, deliver transactional communications, and pursue the
            legitimate interest of protecting the PromptShare community. Optional marketing or engagement
            outreach is contingent upon the user&apos;s consent, which may be withdrawn via the mechanisms made
            available within the platform or by contacting PromptShare.
          </p>
        </section>

        <section className="space-y-2">
          <h2>5. Sharing and Disclosure</h2>
          <p>
            PromptShare may transfer personal information to cloud hosting providers, analytics vendors,
            payment processors, security consultants, professional advisors, or competent authorities when
            legal obligations mandate such disclosure. Each recipient is bound by contractual safeguards and
            is permitted to process data solely for delegated functions. PromptShare disclaims
            responsibility for the practices of third-party websites linked by users within their prompt
            submissions.
          </p>
        </section>

        <section className="space-y-2">
          <h2>6. International Transfers</h2>
          <p>
            Where data is exported beyond the jurisdiction of collection, PromptShare implements recognised
            transfer mechanisms, including adequacy determinations, contractual clauses, or comparable
            protective instruments. Users situated in regions with extraterritorial data rules acknowledge
            that their information may be processed in locations that provide different regulatory
            protection standards.
          </p>
        </section>

        <section className="space-y-2">
          <h2>7. Retention</h2>
          <p>
            Personal data is retained for the duration necessary to fulfil the purposes described herein or
            to comply with statutory retention obligations. Content voluntarily deleted by its uploader may
            persist within operational backups for a limited remediation window before being irreversibly
            purged.
          </p>
        </section>

        <section className="space-y-2">
          <h2>8. Security Measures</h2>
          <p>
            PromptShare deploys proportionate administrative, technical, and organisational safeguards,
            including encrypted transport protocols, access controls, logging, and periodic vulnerability
            assessments. No system is impregnable, and PromptShare expressly disclaims liability for
            unauthorised access or disclosure arising from circumstances beyond its reasonable control.
          </p>
        </section>

        <section className="space-y-2">
          <h2>9. User Responsibilities and Community Oversight</h2>
          <p>
            Uploaders bear full accountability for ensuring that content shared on PromptShare complies with
            all privacy, publicity, and intellectual property obligations. Users are encouraged to respect
            the collaborative ethos of the platform by reporting material that appears unlawful, invasive,
            or otherwise inappropriate. PromptShare typically evaluates reports promptly and endeavours to
            assist, yet makes no representation regarding resolution timelines or outcomes.
          </p>
        </section>

        <section className="space-y-2">
          <h2>10. Individual Rights</h2>
          <p>
            Depending on governing law, you may possess rights of access, rectification, erasure,
            restriction, objection, portability, and complaint to a supervisory authority. Requests should
            be directed to the PromptShare support team using the contact pathway identified herein. PromptShare
            may require identity verification and may deny requests where statutory exemptions apply.
          </p>
        </section>

        <section className="space-y-2">
          <h2>11. Cookies and Similar Technologies</h2>
          <p>
            PromptShare utilises strictly necessary cookies to stabilise session operations and optional
            analytical tools to evaluate platform performance. Users may adjust preferences through browser
            settings or any consent banner furnished. Disabling certain cookies could degrade service
            functionality.
          </p>
        </section>

        <section className="space-y-2">
          <h2>12. Policy Modifications</h2>
          <p>
            PromptShare may revise this Privacy Policy at any time and without prior notice. The updated
            version will supersede all previous iterations upon publication on the platform. Continued use
            after revisions constitutes irrevocable acknowledgement of the amended terms, and users are
            responsible for reviewing updates periodically.
          </p>
        </section>

        <section className="space-y-2">
          <h2>13. Liability Disclaimer</h2>
          <p>
            PromptShare is not liable for damages originating from user-generated content, external links,
            or reliance on prompts shared by others. Uploader negligence, intentional misconduct, or
            statutory non-compliance remains the sole responsibility of the contributing individual.
          </p>
        </section>

        <section className="space-y-2">
          <h2>14. Contact</h2>
          <p>
            Privacy-related inquiries, rights requests, or incident reports may be submitted through the
            contact facility within the platform. PromptShare will respond as soon as practicable, subject to
            resource constraints and legal obligations.
          </p>
        </section>

        <section className="space-y-2">
          <h2>15. California Privacy (CCPA/CPRA) – Do Not Sell or Share</h2>
          <p>
            For California residents, PromptShare provides the rights and disclosures required by the
            California Consumer Privacy Act (as amended by the CPRA). PromptShare does not knowingly “sell”
            personal information as that term is defined under the CPRA, nor does it “share” personal
            information for cross-context behavioral advertising. If these practices change, PromptShare will
            update this Policy and furnish an opt-out mechanism, including recognition of Global Privacy
            Control (GPC) signals where applicable. California residents may exercise access, deletion,
            correction, and other CPRA rights through platform controls or by using the contact pathway set
            forth above. Non-discrimination is observed for users exercising their rights.
          </p>
        </section>

        <section className="space-y-2">
          <h2>16. Children’s Privacy</h2>
          <p>
            The service is not directed to children under the age threshold established by applicable law
            (typically 16). PromptShare does not knowingly collect personal information from children. If you
            believe that a child has provided personal data to PromptShare, please submit a report via the
            platform contact facility; PromptShare will remove the material and delete associated personal
            information as required by law.
          </p>
        </section>
      </div>
    </main>
  );
}
